import dayjs, { Dayjs } from "dayjs";

import { getPeriodDateRange } from "./getPeriodDateRange";

import { DATE } from "@/shared/model/const/services";
import { DateFilterPeriod, DateFilterType } from "@/shared/model/types/date";

export type StoredDateRange = [string | null, string | null];

export type SessionDateSelection =
    | {
          mode: "preset";
          period: DateFilterPeriod;
      }
    | {
          mode: "custom";
          dates: StoredDateRange;
      };

const periods = new Set<DateFilterPeriod>([
    "today",
    "yesterday",
    "this_week",
    "last_week",
    "this_month",
    "last_month",
    "last_7_days",
    "last_14_days",
    "last_30_days",
    "last_90_days",
    "this_year",
]);

const isValidPeriod = (value: unknown): value is DateFilterPeriod =>
    typeof value === "string" && periods.has(value as DateFilterPeriod);

const isValidDateRange = (value: unknown): value is StoredDateRange => {
    if (!Array.isArray(value) || value.length !== 2) return false;

    const [start, end] = value;
    const isValidValue = (date: unknown) =>
        date === null || (typeof date === "string" && dayjs(date, DATE.FORMAT, true).isValid());

    return isValidValue(start) && isValidValue(end) && Boolean(start || end);
};

export const parseDateSelection = (value: unknown): SessionDateSelection | null => {
    if (isValidDateRange(value)) {
        return { mode: "custom", dates: value };
    }

    if (!value || typeof value !== "object") return null;

    const selection = value as Partial<SessionDateSelection>;

    if (selection.mode === "preset" && isValidPeriod(selection.period)) {
        return { mode: "preset", period: selection.period };
    }

    if (selection.mode === "custom" && isValidDateRange(selection.dates)) {
        return { mode: "custom", dates: selection.dates };
    }

    return null;
};

export const getSessionDateSelection = (key: string): SessionDateSelection | null => {
    try {
        return parseDateSelection(JSON.parse(sessionStorage.getItem(key) || "null"));
    } catch {
        sessionStorage.removeItem(key);
        return null;
    }
};

export const saveSessionDateSelection = (key: string, selection: SessionDateSelection) => {
    sessionStorage.setItem(key, JSON.stringify(selection));
};

export const createCustomDateSelection = (date: DateFilterType): SessionDateSelection => {
    const serializedDate: StoredDateRange = date.map((value) =>
        value ? value.format(DATE.FORMAT) : null,
    ) as StoredDateRange;

    return { mode: "custom", dates: serializedDate };
};

export const resolveSessionDateSelection = (
    selection: SessionDateSelection,
    now: Dayjs = dayjs(),
): DateFilterType => {
    if (selection.mode === "preset") {
        return getPeriodDateRange(selection.period, now);
    }

    return selection.dates.map((date) =>
        date ? dayjs(date, DATE.FORMAT, true) : null,
    ) as DateFilterType;
};

export const clearSessionDateRange = (key: string) => {
    sessionStorage.removeItem(key);
};
